import { BitkitExpandableCard, BitkitLabeledData, useResponsive } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Separator } from '@chakra-ui/react/separator';
import { Table } from '@chakra-ui/react/table';
import { Text } from '@chakra-ui/react/text';
import { Fragment, useState } from 'react';

import { DecoratedArtifactDevices } from '@/models/Artifact';

import { useArtifactTracking } from '../../ArtifactTrackingProvider/ArtifactTrackingProvider';

export interface ArtifactDeviceListProps {
  devices: DecoratedArtifactDevices;
}

const ArtifactDeviceList = ({
  devices: { users, userCount: associatedUserCount, deviceCount },
}: ArtifactDeviceListProps) => {
  const { isMobile } = useResponsive();
  const segmentTrack = useArtifactTracking();
  const userCount = Object.keys(users).length;
  const [isExpanded, setIsExpanded] = useState(false);

  const labels = {
    user: 'User',
    devices: 'Devices',
    noDevices: 'No devices',
  };

  const onChange = (isOpened: boolean) => {
    setIsExpanded(isOpened);
    if (isOpened) {
      segmentTrack('Artifact Device List Expanded');
    }
  };

  const getContent = () => {
    if (isMobile) {
      if (deviceCount === 0) {
        return <Text color="text/primary">{labels.noDevices}</Text>;
      }

      return Object.entries(users).map(([userName, userDevices], userIndex) => (
        <Fragment key={userName}>
          <Box display="flex" flexDirection="column" gap="24">
            <BitkitLabeledData label={labels.user} value={userName} />
            {userDevices.map(({ name, udid }, userDeviceIndex) => {
              if (name && udid) {
                return (
                  <BitkitLabeledData
                    key={udid}
                    label={userDeviceIndex === 0 ? labels.devices : ''}
                    value={name}
                    subtext={udid}
                  />
                );
              }

              return null;
            })}
          </Box>
          {userIndex !== userCount - 1 && <Separator marginBlock="24" />}
        </Fragment>
      ));
    }

    return (
      <Table.ScrollArea>
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>{labels.user}</Table.ColumnHeader>
              <Table.ColumnHeader>{labels.devices}</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {userCount > 0 ? (
              Object.keys(users).map((userName) => (
                <Table.Row key={userName}>
                  <Table.Cell>{userName}</Table.Cell>
                  <Table.Cell>
                    <Box display="flex" flexDirection="column" gap="16">
                      {users[userName].map((device) => (
                        <Box display="flex" flexDirection="column" key={device.udid}>
                          <Text>{device.name}</Text>
                          <Text color="text/tertiary">{device.udid}</Text>
                        </Box>
                      ))}
                    </Box>
                  </Table.Cell>
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Cell colSpan={2}>{labels.noDevices}</Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </Table.ScrollArea>
    );
  };

  const title = (
    <Text as="span">
      Who can install this app?{' '}
      <Text color="text/tertiary" as="span">
        · {associatedUserCount} people with {deviceCount} devices
      </Text>
    </Text>
  );

  return (
    <BitkitExpandableCard elevation={false} expanded={isExpanded} onChange={onChange} title={title}>
      {getContent()}
    </BitkitExpandableCard>
  );
};

export default ArtifactDeviceList;
